import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useUrlBool } from '@/hooks/urlState';
import { Checkbox } from '@/design-system/checkbox';
import { toast } from '@/design-system/toaster';
import { Barrier, barriersAPI } from '@/services/api/barriers';
import { Hub, hubsAPI, pitstopToHub } from '@/services/api/hubs';
import {
  Pitstop,
  PitstopType,
  getListPitstop,
  getPitstopById,
} from '@/services/api/pitstops';
import type { ZoneDriver } from '@/services/api/zoneDrivers';
import {
  CellConflict,
  Zone,
  ZoneCellConflictError,
  zonesAPI,
} from '@/services/api/zones';

import ActiveZoneChip from './components/ActiveZoneChip';
import ConflictBanner from './components/ConflictBanner';
import HubPickerDropdown from './components/HubPickerDropdown';
import MapLegend from './components/MapLegend';
import ZoneFormModal from './components/ZoneFormModal';
import ZoneMap, {
  BarrierOverlay,
  ZoneMapHub,
  ZoneOverlay,
} from './components/ZoneMap';
import {
  ZoneDriverBadge,
  ZoneDriverPopover,
} from './components/ZoneDriverBadge';
import ZoneDriversDrawer from './components/ZoneDriversDrawer';
import ZonesFloatingPanel from './components/ZonesFloatingPanel';
import { useCanEditZones } from './useCanEditZones';

/**
 * A map click landing within this window after an interaction inside a
 * floating chip is Google Maps' delayed re-delivery of that same gesture
 * (double-click disambiguation ≈300ms), never a real map click.
 */
const OVERLAY_ECHO_MS = 400;

const ZonesPage: React.FC = () => {
  // ── URL deep-link (?hub=ID from Hubs page) ─────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();

  // Zone records are allowlisted; mitra rosters are not (see
  // `config/zone-permissions`).
  const canEdit = useCanEditZones();

  // ── Hubs ─────────────────────────────────────────────────────────
  const [hubs, setHubs] = useState<Hub[]>([]);
  // Client pitstops (PROVIDER_WAREHOUSE) mapped to the Hub shape — zones can
  // be drawn for them too (client-pitstop pickup TRD D8). Everything stays
  // keyed on `?hub=<id>`; the zone endpoints accept a pitstop id as-is.
  const [clientPitstops, setClientPitstops] = useState<Hub[]>([]);
  const [pitstopClientNames, setPitstopClientNames] = useState<
    Record<number, string>
  >({});
  // Zone tally per hub — one grouped read, so the picker can lead with the
  // handful of locations that actually have zones instead of all ~100.
  const [zoneCounts, setZoneCounts] = useState<Record<number, number>>({});
  const [hubsLoading, setHubsLoading] = useState(true);
  const [activeHubId, setActiveHubId] = useState<number | null>(null);
  // View toggles are URL-backed so a map view is shareable. `hub` is already
  // synced (see handlePickHub); these join it.
  const [showOtherCoverage, setShowOtherCoverage] = useUrlBool(
    'otherCoverage',
    false
  );

  // ── Dispatch barriers (global, read-only "district" layer) ────────
  const [barriers, setBarriers] = useState<Barrier[]>([]);
  const [showBarriers, setShowBarriers] = useUrlBool('barriers', true);

  // ── Zones for the active hub ──────────────────────────────────────
  const [zones, setZones] = useState<Zone[]>([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [activeZoneId, setActiveZoneId] = useState<string | null>(null);

  // ── Cells per zone (drives the map overlays) ──────────────────────
  const [cellsByZone, setCellsByZone] = useState<Record<string, string[]>>({});
  const [cellsLoadingZones, setCellsLoadingZones] = useState<Set<string>>(
    new Set()
  );

  // ── Other hubs' cells (for the boundary-verification toggle) ──────
  const [otherCoverageCells, setOtherCoverageCells] = useState<
    Record<string, { color: string; cells: string[]; hubId: number }>
  >({});
  // Hub whose coverage layer has been requested. An empty result is a
  // legitimate answer ("no other hub has active zones yet"), so emptiness must
  // NOT double as "not loaded" — that read fired a fresh fetch on every commit
  // and hammered the API with one request per other hub, forever. Set before
  // the request starts, so it guards the in-flight window too.
  const otherCoverageHubRef = useRef<number | null>(null);

  // ── Driver roster per zone (master data, Fase 1) — "mitra" in the UI ─
  // One hub-wide fetch hydrates every badge AND every popover, so clicking a
  // badge never triggers a request.
  const [driversByZone, setDriversByZone] = useState<Record<string, ZoneDriver[]>>(
    {}
  );
  const [driversLoading, setDriversLoading] = useState(false);
  const [showDrivers, setShowDrivers] = useUrlBool('drivers', true);
  /** Zone whose roster popover is open on the map. */
  const [rosterZoneId, setRosterZoneId] = useState<string | null>(null);
  /** Zone whose roster is being edited in the drawer. */
  const [driversDrawerZoneId, setDriversDrawerZoneId] = useState<string | null>(
    null
  );

  // ── Modal + transient state ───────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [conflicts, setConflicts] = useState<CellConflict[]>([]);
  const [conflictWorking, setConflictWorking] = useState(false);

  // ── Debounced batching of cell edits ──────────────────────────────
  const pendingAddsRef = useRef<Set<string>>(new Set());
  const pendingRemovesRef = useRef<Set<string>>(new Set());
  const pendingZoneIdRef = useRef<string | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When ops interacts with a floating chip (badge / popover buttons), Google
  // Maps still delivers its own 'click' for that gesture ~300ms later
  // (double-click disambiguation). The DOM-target/stamp filters in ZoneMap
  // catch most of those, but Maps can hand back a native event the stamps
  // never touched — so any map click this soon after a chip interaction is
  // treated as that echo and ignored wholesale. This is what made the badge
  // need two clicks: the echo dismissed the popover the first click opened.
  const overlayInteractionAtRef = useRef(0);
  const FLUSH_DELAY_MS = 500;

  // Active location can be a Dash hub OR a client pitstop — both carry the
  // Hub shape, so everything downstream (map pin, zone fetch) works as-is.
  const activeHub = useMemo(
    () =>
      hubs.find((h) => h.id === activeHubId) ??
      clientPitstops.find((p) => p.id === activeHubId) ??
      null,
    [hubs, clientPitstops, activeHubId]
  );
  const activeZone = useMemo(
    () => zones.find((z) => z.id === activeZoneId) ?? null,
    [zones, activeZoneId]
  );
  const zonesById = useMemo(() => {
    const m = new Map<string, Zone>();
    for (const z of zones) m.set(z.id, z);
    return m;
  }, [zones]);

  // ── Loaders ────────────────────────────────────────────────────────

  const fetchHubs = useCallback(async () => {
    setHubsLoading(true);
    try {
      // Client pitstops are best-effort: the zones editor must keep working
      // for Dash hubs even when the pitstop masterdata call fails. Logged
      // rather than silent — in an environment with no Dash hub this list is
      // the picker's ONLY source, and a swallowed failure there is
      // indistinguishable from "there is no masterdata".
      const [data, pitstopRes, summary] = await Promise.all([
        hubsAPI.list('active'),
        getListPitstop({
          type: PitstopType.PROVIDER_WAREHOUSE,
          page: '1',
          size: '100',
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[ZonesPage] client pitstop list unavailable', err);
          return null;
        }),
        // Best-effort too: without it the picker just loses its grouping.
        zonesAPI.hubSummary().catch(() => []),
      ]);
      setZoneCounts(
        Object.fromEntries(summary.map((row) => [row.hubId, row.zoneCount]))
      );
      const rawPitstops: Pitstop[] = pitstopRes?.data ?? [];
      // Same field mapping hubsAPI applies to HUB pitstops — keeps the rest
      // of the page blind to which section the id came from.
      let pitstopHubs: Hub[] = rawPitstops.map(pitstopToHub);
      let hubList: Hub[] = data;
      const clientNames: Record<number, string> = Object.fromEntries(
        rawPitstops.map((p) => [p.id, p.provider?.name ?? ''])
      );

      // The two lists above are only the FIRST PAGE of each type. Anything
      // past it is invisible, and the ids this page must be able to open are
      // known up front — so fetch those by id instead of hoping they landed
      // in the page: the deep-linked `?hub=`, and every hub that already has
      // zones drawn (a hub with a zone map that can't be opened in the zone
      // editor is the worst kind of missing).
      const loaded = new Set([...hubList, ...pitstopHubs].map((h) => h.id));
      const paramRaw = searchParams.get('hub');
      const paramId = paramRaw ? Number(paramRaw) : NaN;
      const wanted = [
        ...summary.map((row) => row.hubId),
        ...(Number.isFinite(paramId) ? [paramId] : []),
      ].filter((id) => id > 0 && !loaded.has(id));

      if (wanted.length > 0) {
        // Per-id catch: one deleted pitstop still referenced by a zone must
        // not blank out the picker.
        const fetched = await Promise.all(
          Array.from(new Set(wanted)).map((id) =>
            getPitstopById(id).catch(() => null)
          )
        );
        const extraHubs: Hub[] = [];
        const extraPitstops: Hub[] = [];
        for (const p of fetched) {
          if (!p) continue;
          if (p.pitstopType === PitstopType.HUB) extraHubs.push(pitstopToHub(p));
          else {
            extraPitstops.push(pitstopToHub(p));
            clientNames[p.id] = p.provider?.name ?? '';
          }
        }
        hubList = [...hubList, ...extraHubs];
        pitstopHubs = [...pitstopHubs, ...extraPitstops];
      }

      setHubs(hubList);
      setClientPitstops(pitstopHubs);
      setPitstopClientNames(clientNames);
      setActiveHubId((prev) => {
        const all = [...hubList, ...pitstopHubs];
        if (Number.isFinite(paramId) && all.some((h) => h.id === paramId))
          return paramId;
        if (prev !== null && all.some((h) => h.id === prev)) return prev;
        return hubList[0]?.id ?? all[0]?.id ?? null;
      });
    } catch (err) {
      toast.error('Could not load hubs', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setHubsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Barriers are global (not hub-scoped) and read-only, so one fetch per
  // page visit. Non-fatal on error: the zone editor must keep working even
  // if the barrier layer can't load.
  const fetchBarriers = useCallback(async () => {
    try {
      const data = await barriersAPI.list('active');
      setBarriers(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ZonesPage] barrier layer unavailable', err);
      setBarriers([]);
    }
  }, []);

  // One request loads the whole page: zones + every zone's cells
  // (`include=cells`) + every roster (`drivers` is always embedded). The old
  // shape was N+2 requests for N zones (zones, per-zone cells, zone-drivers).
  const fetchZones = useCallback(async (hubId: number) => {
    setZonesLoading(true);
    setDriversLoading(true);
    try {
      const data = await zonesAPI.listByHub(hubId, 'all', 'cells,drivers');
      setZones(data);
      setActiveZoneId((prev) => {
        if (prev && data.some((z) => z.id === prev)) return prev;
        return data[0]?.id ?? null;
      });
      const cells: Record<string, string[]> = {};
      const rosters: Record<string, ZoneDriver[]> = {};
      for (const z of data) {
        cells[z.id] = z.h3Indexes ?? [];
        rosters[z.id] = z.drivers;
      }
      setCellsByZone(cells);
      setDriversByZone(rosters);
    } catch (err) {
      setZones([]);
      setDriversByZone({});
      toast.error('Could not load zones', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setZonesLoading(false);
      setDriversLoading(false);
    }
  }, []);

  const fetchCells = useCallback(async (zoneId: string) => {
    setCellsLoadingZones((prev) => {
      const next = new Set(prev);
      next.add(zoneId);
      return next;
    });
    try {
      const data = await zonesAPI.listCells(zoneId);
      setCellsByZone((prev) => ({ ...prev, [zoneId]: data.h3Indexes }));
    } catch (err) {
      toast.error('Could not load zone cells', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setCellsLoadingZones((prev) => {
        const next = new Set(prev);
        next.delete(zoneId);
        return next;
      });
    }
  }, []);

  const fetchOtherCoverage = useCallback(
    async (activeId: number, knownHubs: Hub[]) => {
      const others = knownHubs.filter((h) => h.id !== activeId);
      try {
        // include=cells → one request per hub instead of one per zone.
        const perHub = await Promise.all(
          others.map((h) =>
            zonesAPI.listByHub(h.id, 'active', 'cells').then((zs) =>
              zs.map((z) => ({
                zoneId: z.id,
                hubId: h.id,
                color: z.color,
                cells: z.h3Indexes ?? [],
              }))
            )
          )
        );
        const flat = perHub.flat();
        const map: Record<
          string,
          { color: string; cells: string[]; hubId: number }
        > = {};
        for (const entry of flat) {
          map[entry.zoneId] = {
            color: entry.color,
            cells: entry.cells,
            hubId: entry.hubId,
          };
        }
        setOtherCoverageCells(map);
      } catch (err) {
        // Release the guard so toggling the layer off and on retries, instead
        // of leaving it permanently stuck on a transient failure.
        otherCoverageHubRef.current = null;
        toast.error("Could not load other hubs' coverage", {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    },
    []
  );

  useEffect(() => {
    fetchHubs();
  }, [fetchHubs]);

  useEffect(() => {
    fetchBarriers();
  }, [fetchBarriers]);

  useEffect(() => {
    if (activeHubId === null) return;
    fetchZones(activeHubId);
    // Switching hubs invalidates the coverage layer — the guard has to drop
    // with it so the new hub's layer can load.
    setOtherCoverageCells({});
    otherCoverageHubRef.current = null;
    setRosterZoneId(null);
  }, [activeHubId, fetchZones]);

  useEffect(() => {
    for (const z of zones) {
      if (!(z.id in cellsByZone) && !cellsLoadingZones.has(z.id)) {
        fetchCells(z.id);
      }
    }
  }, [zones, cellsByZone, cellsLoadingZones, fetchCells]);

  useEffect(() => {
    if (activeHubId === null) return;
    if (!showOtherCoverage) return;
    // Fetch once per hub. `otherCoverageCells` is deliberately NOT a dep: the
    // fetch writes to it, so reading it here to decide whether to fetch is a
    // self-triggering loop.
    if (otherCoverageHubRef.current === activeHubId) return;
    otherCoverageHubRef.current = activeHubId;
    fetchOtherCoverage(activeHubId, hubs);
  }, [showOtherCoverage, activeHubId, hubs, fetchOtherCoverage]);

  // ── Debounced flush for cell edits ────────────────────────────────

  const flushPending = useCallback(async () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const zoneId = pendingZoneIdRef.current;
    if (!zoneId) return;
    const adds = Array.from(pendingAddsRef.current);
    const removes = Array.from(pendingRemovesRef.current);
    if (adds.length === 0 && removes.length === 0) return;

    pendingAddsRef.current = new Set();
    pendingRemovesRef.current = new Set();
    pendingZoneIdRef.current = null;

    try {
      if (removes.length > 0) {
        await zonesAPI.removeCells(zoneId, removes);
      }
      if (adds.length > 0) {
        await zonesAPI.addCells(zoneId, adds);
      }
      setZones((prev) =>
        prev.map((z) =>
          z.id === zoneId
            ? {
                ...z,
                cellCount: Math.max(
                  0,
                  z.cellCount + adds.length - removes.length
                ),
              }
            : z
        )
      );
    } catch (err) {
      if (err instanceof ZoneCellConflictError) {
        const addSet = new Set(adds);
        setCellsByZone((prev) => ({
          ...prev,
          [zoneId]: (prev[zoneId] ?? []).filter((c) => !addSet.has(c)),
        }));
        setConflicts(err.conflicts);
      } else {
        const addSet = new Set(adds);
        setCellsByZone((prev) => {
          const base = (prev[zoneId] ?? []).filter((c) => !addSet.has(c));
          const restored = new Set(base);
          for (const r of removes) restored.add(r);
          return { ...prev, [zoneId]: Array.from(restored) };
        });
        toast.error('Could not save cell changes', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(() => {
      flushPending();
    }, FLUSH_DELAY_MS);
  }, [flushPending]);

  const handlePickHub = useCallback(
    (hubId: number) => {
      flushPending();
      setActiveHubId(hubId);
      setConflicts([]);
      const next = new URLSearchParams(searchParams);
      next.set('hub', String(hubId));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, flushPending]
  );

  const handleSelectZone = useCallback(
    (zoneId: string) => {
      if (
        pendingZoneIdRef.current &&
        pendingZoneIdRef.current !== zoneId
      ) {
        flushPending();
      }
      setActiveZoneId(zoneId);
    },
    [flushPending]
  );

  useEffect(() => {
    return () => {
      flushPending();
    };
  }, [flushPending]);

  // ── Map overlays ───────────────────────────────────────────────────

  const overlays: ZoneOverlay[] = useMemo(() => {
    const own = zones
      .filter((z) => (cellsByZone[z.id]?.length ?? 0) > 0)
      .map((z) => ({
        id: z.id,
        code: z.code,
        color: z.color,
        cells: cellsByZone[z.id] ?? [],
        active: z.id === activeZoneId,
      }));

    if (!showOtherCoverage) return own;

    const others = Object.entries(otherCoverageCells).map(([zoneId, info]) => ({
      id: zoneId,
      code: '',
      color: '#0f766e',
      cells: info.cells,
      active: false,
    }));
    return [...others, ...own];
  }, [zones, cellsByZone, activeZoneId, showOtherCoverage, otherCoverageCells]);

  const otherHubs: ZoneMapHub[] = useMemo(() => {
    if (!showOtherCoverage || !activeHub) return [];
    return hubs
      .filter((h) => h.id !== activeHub.id)
      .map((h) => ({
        id: String(h.id),
        code: h.code,
        name: h.name,
        latitude: h.latitude,
        longitude: h.longitude,
      }));
  }, [showOtherCoverage, hubs, activeHub]);

  const barrierOverlays: BarrierOverlay[] = useMemo(() => {
    if (!showBarriers) return [];
    return barriers.map((b) => ({
      id: b.id,
      code: b.code,
      h3Indexes: b.h3Indexes,
    }));
  }, [showBarriers, barriers]);

  const activeZoneCellCount = activeZoneId
    ? (cellsByZone[activeZoneId]?.length ?? 0)
    : 0;

  // Per-zone cell counts derived from the cells actually loaded for the map
  // overlays. The API's `zone.cellCount` comes back 0, so we prefer the live
  // loaded length and fall back to the API value only until cells arrive.
  const cellCountByZone = useMemo(() => {
    const m: Record<string, number> = {};
    for (const z of zones) {
      const loaded = cellsByZone[z.id];
      m[z.id] = loaded ? loaded.length : z.cellCount;
    }
    return m;
  }, [zones, cellsByZone]);

  const driverCountByZone = useMemo(() => {
    const m: Record<string, number> = {};
    for (const z of zones) m[z.id] = driversByZone[z.id]?.length ?? 0;
    return m;
  }, [zones, driversByZone]);

  // Badges anchor to cells, so a zone with no cells has nowhere to sit on the
  // map — the floating panel is where those get their count.
  const badgeZoneIds = useMemo(() => {
    if (!showDrivers) return [];
    return zones
      .filter((z) => (cellsByZone[z.id]?.length ?? 0) > 0)
      .map((z) => z.id);
  }, [showDrivers, zones, cellsByZone]);

  const driversDrawerZone = useMemo(
    () => zones.find((z) => z.id === driversDrawerZoneId) ?? null,
    [zones, driversDrawerZoneId]
  );

  const openDriversDrawer = useCallback((zoneId: string) => {
    setRosterZoneId(null);
    setDriversDrawerZoneId(zoneId);
  }, []);

  const handleRosterSaved = useCallback(
    (zoneId: string, saved: ZoneDriver[]) => {
      setDriversByZone((prev) => ({ ...prev, [zoneId]: saved }));
    },
    []
  );

  const renderZoneBadge = useCallback(
    (zoneId: string) => {
      const zone = zonesById.get(zoneId);
      if (!zone) return null;
      return (
        <ZoneDriverBadge
          count={driversByZone[zoneId]?.length ?? 0}
          color={zone.color}
          code={zone.code}
          open={rosterZoneId === zoneId}
          onClick={() => {
            overlayInteractionAtRef.current = Date.now();
            setRosterZoneId((prev) => (prev === zoneId ? null : zoneId));
          }}
        />
      );
    },
    [zonesById, driversByZone, rosterZoneId]
  );

  const renderZonePopover = useCallback(
    (zoneId: string) => {
      const zone = zonesById.get(zoneId);
      if (!zone) return null;
      return (
        <ZoneDriverPopover
          code={zone.code}
          name={zone.name}
          color={zone.color}
          drivers={driversByZone[zoneId] ?? []}
          isLoading={driversLoading}
          onClose={() => {
            overlayInteractionAtRef.current = Date.now();
            setRosterZoneId(null);
          }}
          onManage={() => {
            overlayInteractionAtRef.current = Date.now();
            openDriversDrawer(zoneId);
          }}
        />
      );
    },
    [zonesById, driversByZone, driversLoading, openDriversDrawer]
  );

  // ── Core editor action: toggle a cell on the active zone ───────────

  const handleCellClick = useCallback(
    (h3Index: string) => {
      // Delayed echo of a click that happened inside a chip — see
      // overlayInteractionAtRef. Never a cell edit, never a dismiss.
      if (Date.now() - overlayInteractionAtRef.current < OVERLAY_ECHO_MS) {
        return;
      }

      // With a roster popover open, a map click just dismisses it — editing a
      // cell in the same gesture would be a surprise.
      if (rosterZoneId) {
        setRosterZoneId(null);
        return;
      }

      // Painting cells IS editing the zone, so it sits behind the same gate as
      // create/edit/delete — otherwise the restriction would only cover the
      // buttons while the map stayed fully writable.
      if (!canEdit) {
        toast.error('Zone editing is restricted', {
          description:
            'You can manage mitra per zone, but changing zone coverage needs an authorised account.',
        });
        return;
      }

      if (!activeZoneId || !activeZone) {
        toast.error('Pick a zone first', {
          description: 'Select a zone in the panel on the right.',
        });
        return;
      }

      if (
        pendingZoneIdRef.current &&
        pendingZoneIdRef.current !== activeZoneId
      ) {
        flushPending();
      }
      pendingZoneIdRef.current = activeZoneId;

      const currentCells = cellsByZone[activeZoneId] ?? [];
      const inActiveZone = currentCells.includes(h3Index);

      if (inActiveZone) {
        if (pendingAddsRef.current.has(h3Index)) {
          pendingAddsRef.current.delete(h3Index);
        } else {
          pendingRemovesRef.current.add(h3Index);
        }
        setCellsByZone((prev) => ({
          ...prev,
          [activeZoneId]: (prev[activeZoneId] ?? []).filter(
            (c) => c !== h3Index
          ),
        }));
        scheduleFlush();
        return;
      }

      // Within a hub, cells stay disjoint: if another zone OF THIS HUB already
      // owns the cell, raise a conflict (resolved via the reassign flow). Cells
      // owned by OTHER hubs are not a conflict — hubs may overlap — so the
      // "other coverage" layer is purely informational and never blocks a click.
      let ownerZoneId: string | null = null;
      for (const z of zones) {
        if (z.id === activeZoneId) continue;
        if ((cellsByZone[z.id] ?? []).includes(h3Index)) {
          ownerZoneId = z.id;
          break;
        }
      }
      if (ownerZoneId) {
        const ownerZone = zonesById.get(ownerZoneId);
        setConflicts([
          {
            h3Index,
            existingZone: {
              id: ownerZoneId,
              code: ownerZone?.code ?? '',
              hubId: ownerZone?.hubId ?? activeZone.hubId,
            },
          },
        ]);
        return;
      }

      if (pendingRemovesRef.current.has(h3Index)) {
        pendingRemovesRef.current.delete(h3Index);
      } else {
        pendingAddsRef.current.add(h3Index);
      }
      setCellsByZone((prev) => ({
        ...prev,
        [activeZoneId]: [...(prev[activeZoneId] ?? []), h3Index],
      }));
      scheduleFlush();
    },
    [
      activeZoneId,
      activeZone,
      cellsByZone,
      zones,
      zonesById,
      rosterZoneId,
      canEdit,
      flushPending,
      scheduleFlush,
    ]
  );

  const handleReassign = useCallback(async () => {
    if (!activeZoneId || conflicts.length === 0) return;
    if (!canEdit) return;
    await flushPending();
    setConflictWorking(true);
    try {
      const byOwner = new Map<string, string[]>();
      for (const c of conflicts) {
        const list = byOwner.get(c.existingZone.id) ?? [];
        list.push(c.h3Index);
        byOwner.set(c.existingZone.id, list);
      }
      const ownerEntries = Array.from(byOwner.entries());
      for (const [ownerId, cells] of ownerEntries) {
        await zonesAPI.removeCells(ownerId, cells);
      }

      const allCells = conflicts.map((c) => c.h3Index);
      await zonesAPI.addCells(activeZoneId, allCells);

      const touched = new Set<string>([
        activeZoneId,
        ...Array.from(byOwner.keys()),
      ]);
      await Promise.all(Array.from(touched).map((id) => fetchCells(id)));
      if (activeHub) {
        const refreshed = await zonesAPI.listByHub(activeHub.id, 'all');
        setZones(refreshed);
      }
      // The other-hubs layer is deliberately left alone: conflicts only ever
      // involve zones of the ACTIVE hub (cells stay disjoint per hub, and hubs
      // may overlap), so a reassign can't invalidate another hub's coverage.

      setConflicts([]);
      toast.success('Cells reassigned', {
        description: `${allCells.length} cell${
          allCells.length === 1 ? '' : 's'
        } moved.`,
      });
    } catch (err) {
      toast.error('Reassign failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setConflictWorking(false);
    }
  }, [activeZoneId, activeHub, conflicts, canEdit, fetchCells, flushPending]);

  const handleZoneSaved = (saved: Zone) => {
    setZones((prev) => {
      const idx = prev.findIndex((z) => z.id === saved.id);
      if (idx === -1) {
        const next = [...prev, saved];
        next.sort((a, b) => a.code.localeCompare(b.code));
        return next;
      }
      const next = [...prev];
      next[idx] = saved;
      return next;
    });
    // A brand-new zone starts with an empty roster — seed it so the badge and
    // panel read "0 mitra" instead of blank until the next hub-wide fetch.
    setDriversByZone((prev) =>
      saved.id in prev ? prev : { ...prev, [saved.id]: [] }
    );
    setActiveZoneId(saved.id);
  };

  const handleDeleteZone = async (z: Zone) => {
    if (!canEdit) return;
    const count = cellsByZone[z.id]?.length ?? z.cellCount;
    if (count > 0) {
      toast.error('Cannot delete', {
        description: `Remove the ${count} cell${count === 1 ? '' : 's'} first.`,
      });
      return;
    }
    if (!window.confirm(`Delete zone ${z.code}? This cannot be undone.`)) {
      return;
    }
    try {
      await zonesAPI.delete(z.id);
      setZones((prev) => prev.filter((x) => x.id !== z.id));
      // Deleting a zone cascades to its roster server-side.
      setDriversByZone((prev) => {
        if (!(z.id in prev)) return prev;
        const next = { ...prev };
        delete next[z.id];
        return next;
      });
      if (activeZoneId === z.id) setActiveZoneId(null);
      if (rosterZoneId === z.id) setRosterZoneId(null);
      if (driversDrawerZoneId === z.id) setDriversDrawerZoneId(null);
      toast.success('Zone deleted', { description: `${z.code} removed.` });
    } catch (err) {
      toast.error('Could not delete zone', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-11 items-center border-b border-stroke-soft-200 bg-bg-white-0 px-4">
        <div className="flex min-w-0 items-center gap-3">
          <p className="label-eyebrow whitespace-nowrap">Master · Zone</p>
          <span className="text-text-soft-400">/</span>
          <HubPickerDropdown
            hubs={hubs}
            pitstops={clientPitstops}
            pitstopClientNames={pitstopClientNames}
            zoneCounts={zoneCounts}
            onSearch={hubsAPI.searchLocations}
            activeHubId={activeHubId}
            onPick={handlePickHub}
          />
          <span className="text-text-soft-400">·</span>
          <label
            htmlFor="show-other-coverage"
            className="inline-flex cursor-pointer items-center gap-2 text-[11px] font-medium text-text-sub-600"
          >
            <Checkbox
              id="show-other-coverage"
              size="sm"
              checked={showOtherCoverage}
              onCheckedChange={(v) => setShowOtherCoverage(v === true)}
            />
            Show other hubs' coverage
          </label>
          <span className="text-gray-200">·</span>
          <label className="inline-flex cursor-pointer items-center gap-2 text-[11px] font-medium text-gray-700">
            <input
              type="checkbox"
              checked={showBarriers}
              onChange={(e) => setShowBarriers(e.target.checked)}
              className="h-3.5 w-3.5 rounded-gsm-sm border-gray-300 text-primary focus:ring-primary"
            />
            Show dispatch barriers
          </label>
          <span className="text-text-soft-400">·</span>
          <label
            htmlFor="show-mitra-badges"
            className="inline-flex cursor-pointer items-center gap-2 text-[11px] font-medium text-text-sub-600"
          >
            <Checkbox
              id="show-mitra-badges"
              size="sm"
              checked={showDrivers}
              onCheckedChange={(v) => {
                const on = v === true;
                setShowDrivers(on);
                if (!on) setRosterZoneId(null);
              }}
            />
            Show mitra badges
          </label>
        </div>
      </div>

      <div className="relative flex-1">
        <div className="absolute inset-0">
          <ZoneMap
            hub={
              activeHub
                ? {
                    id: String(activeHub.id),
                    code: activeHub.code,
                    name: activeHub.name,
                    latitude: activeHub.latitude,
                    longitude: activeHub.longitude,
                    active: true,
                  }
                : undefined
            }
            otherHubs={otherHubs}
            overlays={overlays}
            barriers={barrierOverlays}
            onCellClick={handleCellClick}
            activeZoneId={activeZoneId}
            badgeZoneIds={badgeZoneIds}
            renderZoneBadge={renderZoneBadge}
            popoverZoneId={showDrivers ? rosterZoneId : null}
            renderZonePopover={renderZonePopover}
          />
        </div>

        <div className="pointer-events-none absolute inset-0">
          {!activeHub && (
            <div className="pointer-events-auto absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-stroke-soft-200 bg-bg-white-0/95 px-5 py-4 text-center shadow-md backdrop-blur">
              <p className="label-eyebrow mb-1">
                {hubsLoading ? 'Loading…' : 'No hub selected'}
              </p>
              <p className="text-[13px] tracking-tight text-text-sub-600">
                {hubsLoading
                  ? 'Fetching hubs.'
                  : hubs.length === 0
                    ? 'No active hubs yet — create one on the Hubs page.'
                    : 'Pick a hub from the top-left dropdown to start editing.'}
              </p>
            </div>
          )}

          {activeHub && (
            <>
              <div className="pointer-events-auto absolute left-4 top-4">
                <ActiveZoneChip
                  zone={activeZone}
                  cellCount={activeZoneCellCount}
                />
              </div>

              {conflicts.length > 0 && (
                <div className="pointer-events-auto absolute left-1/2 top-4 -translate-x-1/2">
                  <ConflictBanner
                    conflicts={conflicts}
                    zonesById={zonesById}
                    onReassign={handleReassign}
                    onDismiss={() => setConflicts([])}
                    isWorking={conflictWorking}
                  />
                </div>
              )}

              <div className="pointer-events-auto absolute bottom-4 right-4 top-4">
                <ZonesFloatingPanel
                  hub={activeHub}
                  zones={zones}
                  cellCounts={cellCountByZone}
                  driverCounts={driverCountByZone}
                  activeZoneId={activeZoneId}
                  isLoading={zonesLoading}
                  canEditZones={canEdit}
                  onSelectZone={handleSelectZone}
                  onManageDrivers={openDriversDrawer}
                  onNewZone={() => {
                    setEditingZone(null);
                    setFormOpen(true);
                  }}
                  onEditZone={(z) => {
                    setEditingZone(z);
                    setFormOpen(true);
                  }}
                  onDeleteZone={handleDeleteZone}
                />
              </div>

              <div className="pointer-events-auto absolute bottom-4 left-4">
                <MapLegend
                  showOtherCoverage={showOtherCoverage}
                  showBarriers={showBarriers && barriers.length > 0}
                  showDriverBadges={showDrivers}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <ZoneFormModal
        isOpen={formOpen}
        hubId={activeHub?.id ?? 0}
        zone={editingZone}
        onClose={() => {
          setFormOpen(false);
          setEditingZone(null);
        }}
        onSaved={handleZoneSaved}
      />

      <ZoneDriversDrawer
        isOpen={driversDrawerZone !== null}
        zone={driversDrawerZone}
        drivers={
          driversDrawerZoneId ? (driversByZone[driversDrawerZoneId] ?? []) : []
        }
        hubName={activeHub?.name}
        onClose={() => setDriversDrawerZoneId(null)}
        onSaved={handleRosterSaved}
      />
    </div>
  );
};

export default ZonesPage;
